import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, StatusBadge, EmptyState, Skeleton, Button } from '@rb/ui'
import type { DataTableColumn } from '@rb/ui'
import type { ProjectStatus } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import styles from './AdminProjects.module.css'

interface AdminProject {
  id: string
  name: string
  status: string
  updated_at: string
  file_count: number
  client_email: string
  client_name: string | null
}

function formatDate(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function AdminProjects() {
  const navigate = useNavigate()
  const { data, loading, error, reload } = useFetch<AdminProject[]>('/admin/projects')
  const projects = data ?? []

  useEffect(() => {
    window.addEventListener('focus', reload)
    return () => window.removeEventListener('focus', reload)
  }, [reload])

  const columns: DataTableColumn<AdminProject>[] = [
    {
      key: 'name',
      header: 'Projekt',
      render: (row) => <span className={styles.projectName}>{row.name}</span>,
    },
    {
      key: 'client',
      header: 'Kunde',
      render: (row) => (
        <div>
          <div className={styles.clientName}>{row.client_name || row.client_email}</div>
          {row.client_name && <div className={styles.clientEmail}>{row.client_email}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status as ProjectStatus} />,
      width: '180px',
    },
    {
      key: 'file_count',
      header: 'Dateien',
      render: (row) => <span className={styles.fileCount}>{row.file_count}</span>,
      width: '80px',
    },
    {
      key: 'updated_at',
      header: 'Letzte Änderung',
      render: (row) => <span className={styles.date}>{formatDate(row.updated_at)}</span>,
      width: '160px',
    },
  ]

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Alle Projekte</h1>
        <Button variant="secondary" onClick={reload} disabled={loading}>
          Aktualisieren
        </Button>
      </header>

      {loading && (
        <div>
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
          <Skeleton className={styles.skeletonRow} />
        </div>
      )}

      {!loading && error && (
        <div className={styles.errorState}>
          <p>Fehler beim Laden</p>
          <Button variant="secondary" onClick={reload}>Erneut versuchen</Button>
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <EmptyState
          heading="Noch keine Projekte"
          description="Wenn Kunden Projekte hochladen, erscheinen sie hier."
          ctaLabel="Dashboard"
          ctaTo="/dashboard"
        />
      )}

      {!loading && !error && projects.length > 0 && (
        <DataTable
          columns={columns}
          rows={projects}
          getRowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/admin/projects/${row.id}`)}
        />
      )}
    </div>
  )
}
